import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Sparkles, Save, X } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { EmailBuilder } from './emailBuilder/EmailBuilder';
import { PreviewModal } from './emailBuilder/components/PreviewModal';
import { EmailDesign } from './emailBuilder/types';
import { generateHtml, generatePlainText } from './emailBuilder/utils/htmlGenerator';
import { parseHtmlToDesign } from './emailBuilder/utils/htmlParser';
import { MERGE_TAGS } from './emailBuilder/utils/mergeTagHelper';

interface EDMTemplate {
  _id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category: string;
  description?: string;
  isActive: boolean;
  placeholders: string[];
  targetRoles?: string[];
  targetConstructionTypes?: string[];
}

interface Role {
  _id: string;
  name: string;
}

interface ConstructionType {
  _id: string;
  name: string;
}

export const EmailTemplateBuilder: React.FC<{ onUpdate?: () => void }> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  
  // Determine if dark mode is active
  const isDarkMode = theme.mode === 'dark' || 
    (theme.mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ||
    document.documentElement.classList.contains('dark');
  const [templates, setTemplates] = useState<EDMTemplate[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [constructionTypes, setConstructionTypes] = useState<ConstructionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EDMTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<{ subject: string; htmlContent: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [emailDesign, setEmailDesign] = useState<EmailDesign | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    category: 'generic' as 'generic' | 'feature_education' | 'usage_achievement' | 'underutilization' | 'upgrade_prompt',
    description: '',
    targetRoles: [] as string[],
    targetConstructionTypes: [] as string[]
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Wait for auth to finish loading, then only fetch if authenticated
    if (authLoading) {
      return; // Still checking auth
    }
    
    if (isAuthenticated && user) {
      fetchTemplates();
      fetchRoles();
      fetchConstructionTypes();
    } else {
      // Not authenticated - set empty state
      setTemplates([]);
      setRoles([]);
      setConstructionTypes([]);
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated || !user) {
        setTemplates([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setTemplates([]);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setTemplates([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated) {
        setRoles([]);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setRoles([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setRoles(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles([]);
    }
  };

  const fetchConstructionTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated) {
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/construction-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setConstructionTypes([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setConstructionTypes(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching construction types:', error);
      setConstructionTypes([]);
    }
  };

  const handleSave = async () => {
    try {
      // Generate HTML from design if available
      let htmlContent = formData.htmlContent;
      if (emailDesign) {
        htmlContent = generateHtml(emailDesign);
        const textContent = generatePlainText(htmlContent);
        setFormData(prev => ({
          ...prev,
          htmlContent,
          textContent
        }));
      }
      performSave(htmlContent);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const performSave = async (htmlContent: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to save templates.');
        return;
      }

      const isEdit = editingTemplate && editingTemplate._id;
      const url = isEdit
        ? `${apiUrl}/api/edm/templates/${editingTemplate._id}`
        : `${apiUrl}/api/edm/templates`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          htmlContent
        })
      });

      const data = await response.json();

      if (data.success) {
        await fetchTemplates();
        setEditingTemplate(null);
        resetForm();
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    }
  };

  const handlePreview = async (template: EDMTemplate) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/templates/${template._id}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPreviewContent(data.data);
        setShowPreview(true);
      } else {
        alert(data.message || 'Failed to preview template');
      }
    } catch (error) {
      console.error('Error previewing template:', error);
      // Don't redirect on network errors
      alert('Failed to preview template. Please check your connection and try again.');
    }
  };

  const handleDelete = async (templateId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to delete templates.');
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchTemplates();
        setShowDeleteConfirm(null);
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template. Please try again.');
    }
  };

  const handleAIGenerate = async () => {
    try {
      if (!user?._id) {
        alert('User ID required for AI generation');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to generate AI content.');
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/templates/ai-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: formData.category,
          userId: user._id,
          templateName: formData.name || 'Marketing Email',
          targetRoles: formData.targetRoles || [],
          targetConstructionTypes: formData.targetConstructionTypes || []
        })
      });
      
      const data = await response.json();
      if (data.success && data.data) {
        setFormData(prev => ({
          ...prev,
          subject: data.data.subject || prev.subject,
          htmlContent: data.data.htmlContent || prev.htmlContent,
          textContent: data.data.textContent || prev.textContent
        }));
        
        // Parse HTML into EmailDesign structure
        if (data.data.htmlContent) {
          try {
            const design = parseHtmlToDesign(data.data.htmlContent);
            setEmailDesign(design);
          } catch (error) {
            console.error('Error parsing HTML to design:', error);
            // Fallback: create a simple text block with the HTML content
            setEmailDesign({
              blocks: [{
                id: `block-${Date.now()}`,
                type: 'html',
                content: {
                  html: data.data.htmlContent
                }
              }],
              styles: {
                backgroundColor: '#ffffff',
                width: '600px',
                maxWidth: '600px'
              }
            });
          }
        }
      }
    } catch (error) {
      console.error('Error generating AI content:', error);
      alert('Failed to generate AI content');
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      category: 'generic',
      description: '',
      targetRoles: [],
      targetConstructionTypes: []
    });
    setEmailDesign(null);
  };

  const handleEdit = (template: EDMTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      htmlContent: template.htmlContent,
      textContent: template.textContent || '',
      category: template.category as any,
      description: template.description || '',
      targetRoles: template.targetRoles || [],
      targetConstructionTypes: template.targetConstructionTypes || []
    });
    // Parse HTML into EmailDesign structure
    if (template.htmlContent) {
      try {
        const design = parseHtmlToDesign(template.htmlContent);
        setEmailDesign(design);
      } catch (error) {
        console.error('Error parsing HTML to design:', error);
        // Fallback: create a simple HTML block
        setEmailDesign({
          blocks: [{
            id: `block-${Date.now()}`,
            type: 'html',
            content: {
              html: template.htmlContent
            }
          }],
          styles: {
            backgroundColor: '#ffffff',
            width: '600px',
            maxWidth: '600px'
          }
        });
      }
    } else {
      setEmailDesign(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">{t('common.loading') || 'Loading...'}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('edm.templates.title') || 'Templates'}
        </h2>
        {!editingTemplate && (
          <button
            onClick={() => {
              resetForm();
              setEditingTemplate({} as EDMTemplate);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('edm.templates.createTemplate') || 'Create Template'}</span>
          </button>
        )}
      </div>

      {/* Template Form */}
      {editingTemplate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingTemplate._id ? t('edm.templates.editTemplate') || 'Edit Template' : t('edm.templates.createTemplate') || 'Create Template'}
            </h3>
            <button
              onClick={() => {
                setEditingTemplate(null);
                resetForm();
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.templates.name') || 'Template Name'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.templates.category') || 'Category'}
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="generic">{t('edm.templates.categoryGeneric') || 'Generic'}</option>
                <option value="feature_education">{t('edm.templates.categoryFeatureEducation') || 'Feature Education'}</option>
                <option value="usage_achievement">{t('edm.templates.categoryUsageAchievement') || 'Usage Achievement'}</option>
                <option value="underutilization">{t('edm.templates.categoryUnderutilization') || 'Underutilization'}</option>
                <option value="upgrade_prompt">{t('edm.templates.categoryUpgradePrompt') || 'Upgrade Prompt'}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.templates.subject') || 'Email Subject'}
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Welcome {{firstName}}!"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('edm.templates.htmlContent') || 'Email Content'}
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleAIGenerate}
                    className="flex items-center space-x-1 text-sm text-accent-600 dark:text-accent-400 hover:text-accent-700"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{t('edm.templates.aiGenerate') || 'AI Generate'}</span>
                  </button>
                  <button
                    onClick={() => {
                      if (emailDesign) {
                        setPreviewContent({
                          subject: formData.subject,
                          htmlContent: generateHtml(emailDesign)
                        });
                        setShowPreview(true);
                      }
                    }}
                    className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    disabled={!emailDesign}
                  >
                    <Eye className="w-4 h-4" />
                    <span>{t('edm.templates.preview') || 'Preview'}</span>
                  </button>
                </div>
              </div>
              
              <div 
                className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden w-full" 
                style={{ height: 'calc(100vh - 350px)', minHeight: '600px' }}
              >
                <EmailBuilder
                  initialDesign={emailDesign || undefined}
                  onDesignChange={(design) => {
                    setEmailDesign(design);
                    const html = generateHtml(design);
                    const text = generatePlainText(html);
                    setFormData(prev => ({
                      ...prev,
                      htmlContent: html,
                      textContent: text
                    }));
                  }}
                  mergeTags={MERGE_TAGS}
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
              >
                <Save className="w-5 h-5" />
                <span>{t('common.save') || 'Save'}</span>
              </button>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates List */}
      {!editingTemplate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            {templates.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                {t('edm.templates.noTemplates') || 'No templates found'}
              </p>
            ) : (
              <div className="space-y-3">
                {templates.map(template => (
                  <div
                    key={template._id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">{template.name}</h3>
                        <span className="px-2 py-1 text-xs rounded bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200">
                          {template.category}
                        </span>
                        {template.isActive ? (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {t('common.active') || 'Active'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {t('common.inactive') || 'Inactive'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{template.subject}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePreview(template)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title={t('edm.templates.preview') || 'Preview'}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title={t('common.edit') || 'Edit'}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(template._id)}
                        className="p-2 text-error-500 hover:text-error-700"
                        title={t('common.delete') || 'Delete'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewContent && emailDesign && (
        <PreviewModal
          design={emailDesign}
          subject={previewContent.subject}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('edm.templates.deleteConfirm') || 'Delete Template?'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('common.confirmDelete') || 'Are you sure you want to delete this template? This action cannot be undone.'}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700"
              >
                {t('common.delete') || 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
